import { api } from "./client";

// Mirrors app/routers/feature_request.py

export interface FeatureRequest {
  request_id: number;
  created_by: number;
  title: string;
  category: "feature" | "change" | "problem";
  urgent: boolean;
  status: "new" | "accepted" | "in_progress" | "testing" | "done" | "declined";
  eta_at: string | null;
  eta_note: string | null;
  declined_reason: string | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
}

export interface TimelineEntry {
  update_id: number;
  author_role: "operator" | "team";
  note: string;
  new_status: string | null;
  created_at: string;
}

export interface FeatureRequestDetail extends FeatureRequest {
  description: string | null;
  timeline: TimelineEntry[];
}

export const STATUS_LABELS: Record<FeatureRequest["status"], string> = {
  new: "Received",
  accepted: "Approved",
  in_progress: "In progress",
  testing: "Testing",
  done: "Completed",
  declined: "Not planned",
};

export async function listRequests(): Promise<FeatureRequest[]> {
  const resp = await api.get<{ success: boolean; data: FeatureRequest[]; error: string | null }>(
    "/requests/",
  );
  if (!resp.data.success) throw new Error(resp.data.error || "Failed to load requests");
  return resp.data.data;
}

export async function getRequest(id: number): Promise<FeatureRequestDetail> {
  const resp = await api.get<{ success: boolean; data: FeatureRequestDetail; error: string | null }>(
    `/requests/${id}`,
  );
  if (!resp.data.success) throw new Error(resp.data.error || "Failed to load request");
  return resp.data.data;
}

export async function createRequest(body: {
  title: string;
  description?: string;
  category: string;
  urgent: boolean;
}): Promise<{ request_id: number; eta_at: string }> {
  const resp = await api.post<{ success: boolean; data: { request_id: number; eta_at: string }; error: string | null }>(
    "/requests/",
    body,
  );
  if (!resp.data.success) throw new Error(resp.data.error || "Failed to submit request");
  return resp.data.data;
}

export async function addComment(id: number, note: string): Promise<void> {
  const resp = await api.post<{ success: boolean; error: string | null }>(
    `/requests/${id}/comments`,
    { note },
  );
  if (!resp.data.success) throw new Error(resp.data.error || "Failed to add comment");
}

export async function patchRequest(
  id: number,
  body: { status?: string; eta_at?: string; eta_note?: string; declined_reason?: string; note?: string },
): Promise<void> {
  const resp = await api.patch<{ success: boolean; error: string | null }>(`/requests/${id}`, body);
  if (!resp.data.success) throw new Error(resp.data.error || "Failed to update request");
}
